import { useState, useEffect } from "react"
import { Shield, ShieldCheck, Loader2 } from "lucide-react"
import Container from "@/components/Container"
import useAxios from "@/hooks/useAxios"
import { config } from "@/config"
import { useSelector } from "react-redux"
import { ReduxState } from "@/redux/type"

export default function TwoFactorAuth() {
    const axios = useAxios()
    const [is2FAEnabled, setIs2FAEnabled] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [isInitialLoading, setIsInitialLoading] = useState(true)
    const customerId = useSelector((state: ReduxState) => state.customer.cardCode)
    const email = useSelector((state: ReduxState) => state.session.email)

    useEffect(() => {
        fetchTwoFactorStatus()
    }, [customerId])

    async function fetchTwoFactorStatus() {
        try {
            setIsInitialLoading(true)
            // Replace with your actual API endpoint for fetching 2FA status
            const res = await axios.get(`${config.backendUrl}/user-settings`, {
                params: {
                    email: email
                }
            })
            setIs2FAEnabled(res.data.twoFactorAuthentication || false)
        } catch (error) {
            console.error('Failed to fetch 2FA status:', error)
            // Default to false if API call fails
            setIs2FAEnabled(false)
        } finally {
            setIsInitialLoading(false)
        }
    }

    async function toggleTwoFactor() {
        setIsLoading(true)
        try {
            // Replace with your actual API endpoint for toggling 2FA
            const res = await axios.post(`${config.backendUrl}/update-two-factor-authentication`, {
                twoFactorAuthentication: !is2FAEnabled
            })
            
            setIs2FAEnabled(res.data.twoFactorAuthentication)
            
            // You might want to show a success notification here
            console.log(`Two-factor authentication ${res.data.twoFactorAuthentication ? 'enabled' : 'disabled'}`)
        } catch (error) {
            console.error('Failed to toggle 2FA:', error)
            // You might want to show an error notification here
        } finally {
            setIsLoading(false)
        }
    }

    if (isInitialLoading) {
        return (
            <Container>
                <div className="mb-4">
                    <h1 className="font-semibold text-gray-800">Two-Factor Authentication</h1>
                </div>
                
                <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div className="flex items-center gap-4">
                            <div className="p-2 rounded-full bg-gray-100 animate-pulse">
                                <div className="w-6 h-6 bg-gray-300 rounded"></div>
                            </div>
                            <div className="flex-1">
                                <div className="h-5 bg-gray-200 rounded animate-pulse mb-2"></div>
                                <div className="h-4 bg-gray-100 rounded animate-pulse mb-1 w-3/4"></div>
                                <div className="h-3 bg-gray-100 rounded animate-pulse w-1/2"></div>
                            </div>
                        </div>
                        <div className="w-full sm:w-auto">
                            <div className="h-9 bg-gray-200 rounded animate-pulse w-full sm:w-24"></div>
                        </div>
                    </div>
                </div>
                
                <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-start gap-3">
                        <div className="w-5 h-5 bg-blue-200 rounded animate-pulse mt-0.5"></div>
                        <div className="flex-1">
                            <div className="h-4 bg-blue-200 rounded animate-pulse mb-2 w-1/2"></div>
                            <div className="space-y-1">
                                <div className="h-3 bg-blue-100 rounded animate-pulse"></div>
                                <div className="h-3 bg-blue-100 rounded animate-pulse w-5/6"></div>
                                <div className="h-3 bg-blue-100 rounded animate-pulse w-4/6"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </Container>
        )
    }

    return (
        <Container>
            <div className="mb-4">
                <h1 className="font-semibold text-gray-800">Two-Factor Authentication</h1>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex items-start gap-4">
                        <div className={`p-2 rounded-full flex-shrink-0 ${is2FAEnabled ? 'bg-green-100' : 'bg-gray-100'}`}>
                            {is2FAEnabled ? (
                                <ShieldCheck size={24} className="text-green-600" />
                            ) : (
                                <Shield size={24} className="text-gray-600" />
                            )}
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-800 mb-1">
                                Two-Factor Authentication
                            </p>
                            <p className="text-sm text-gray-500 mb-1">
                                {is2FAEnabled 
                                    ? "Your account is secured with 2FA" 
                                    : "Add an extra layer of security to your account"
                                }
                            </p>
                            <p className={`text-xs font-medium ${is2FAEnabled ? 'text-green-600' : 'text-gray-400'}`}>
                                Status: {is2FAEnabled ? "Enabled" : "Disabled"}
                            </p>
                        </div>
                    </div>
                    <div className="w-full sm:w-auto">
                        <button 
                            onClick={isLoading ? undefined : toggleTwoFactor}
                            disabled={isLoading}
                            className={`w-full sm:w-auto text-sm font-medium border shadow-lg shadow-gray-200 py-2.5 px-4 rounded-md transition-colors
                                ${isLoading 
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-300' 
                                    : is2FAEnabled 
                                        ? 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300' 
                                        : 'bg-blue-600 text-white hover:bg-blue-700 border-blue-600'
                                }`}
                        >
                            {isLoading ? (
                                <div className="flex items-center justify-center gap-2">
                                    <Loader2 size={16} className="animate-spin" />
                                    <span>{is2FAEnabled ? "Disabling..." : "Enabling..."}</span>
                                </div>
                            ) : (
                                is2FAEnabled ? "Disable 2FA" : "Enable 2FA"
                            )}
                        </button>
                    </div>
                </div>
            </div>
            
            <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-start gap-3">
                    <Shield size={20} className="text-blue-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-blue-900 mb-2">About Two-Factor Authentication</h3>
                        <p className="text-sm text-blue-700 leading-relaxed">
                            Two-factor authentication adds an extra layer of security to your account. 
                            When enabled, you'll need to provide a verification code sent to your email 
                            in addition to your password when signing in.
                        </p>
                    </div>
                </div>
            </div>
        </Container>
    )
} 